import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  ESTADO_ACTIVA,
  ESTADO_TEMPORAL,
  ESTADO_VENCIDA,
  ESTADOS
} from '../../../constants/tiendaOficial.js'
import { validarRangoFechas } from '../../../helpers/dateValidation.js'
import { toastErrorLog } from '../../../helpers/toastErrorLog.js'
import { useAuth } from '../../../hooks/useAuth'
import { setModal } from '../../../slices/modalSlice.js'
import { formatearFecha } from '../../helpers/helper.js'
import { FechaSelect } from '../../reutilizables/fecha/FechaSelect.jsx'
import GenericSelect from '../../reutilizables/genericSelect/GenericSelect.jsx'
import { GenericModal } from '../../reutilizables/modal/GenericModal.jsx'
import * as publicacionesService from '../../servicios/publicacionesService.js'
import * as tiendasOficialesService from '../../servicios/tiendasOficialesService.js'
import Input from '../../ui/Input.jsx'
import LinkButton from '../../ui/LinkButton.jsx'
import styles from './ConvertirTiendaOficial.module.css'
import Upload from './Upload.jsx'

const FECHA = 10
const MAX_CARACTERES_TITULO_PUB = 40
const MAX_CARACTERES_DESCRIPCION = 300

const ConvertirTiendaOficial = () => {
  const [tienda, setTienda] = useState()
  const [tiendas, setTiendas] = useState([])
  const [publicaciones, setPublicaciones] = useState([])
  const [idsExcluidos, setIdsExcluidos] = useState([])
  const [errores, setErrores] = useState({})
  const [formData, setFormData] = useState([])
  const { loginDataContext: { idUsuario } = {} } = useAuth()
  const dispatch = useDispatch()
  const tiendaOficialInicial = useRef(null)
  const navigate = useNavigate()
  const topRef = useRef(null)

  const obtenerInformacion = useCallback(async () => {
    if (!idUsuario) return
    const { data: tiendaData } = await tiendasOficialesService.buscarPorUsuario(idUsuario)
    const { data: tiendasData } = await tiendasOficialesService.buscarTodas('')
    const { data: publicacionesData } = await publicacionesService.buscarPorVendedor(idUsuario, '')
    setPublicaciones(publicacionesData)
    setTiendas(tiendasData)
    tiendaOficialInicial.current = tiendaData

    if (tiendaData.fechaHasta && validarRangoFechas(new Date(), tiendaData.fechaHasta, 'day')) {
      const excluidas = publicacionesData
        .filter((p) => !p.esTiendaOficial)
        .map((p) => p.idPublicacion)

      setIdsExcluidos(excluidas)
    }
    setTienda({
      ...tiendaData,
      idUsuario,
      fechaDesde: tiendaData.fechaDesde ? formatearFecha(tiendaData.fechaDesde, 'ISO') : '',
      fechaHasta: tiendaData.fechaHasta ? formatearFecha(tiendaData.fechaHasta, 'ISO') : ''
    })
  }, [idUsuario])

  useEffect(() => {
    obtenerInformacion()
  }, [obtenerInformacion])

  const validarForm = () => {
    const nuevosErrores = {}
    if (tienda.nombreTienda === null || tienda.nombreTienda.trim() === '') {
      nuevosErrores.nombreTienda = 'El nombre de la Tienda no puede quedar vacío.'
    } else if (
      tiendas.some(
        (t) =>
          t.nombreTienda.trim().toLowerCase() === tienda.nombreTienda.trim().toLowerCase() &&
          t.idTiendaOficial !== tienda.idTiendaOficial
      )
    ) {
      nuevosErrores.nombreTienda = 'Ya existe una Tienda Oficial con ese nombre.'
    }

    if (tienda.descripcionTienda && tienda.descripcionTienda.length > MAX_CARACTERES_DESCRIPCION) {
      nuevosErrores.descripcionTienda = `La descripción no puede superar los ${MAX_CARACTERES_DESCRIPCION} caracteres.`
    }

    if (!tienda.fechaDesde) {
      nuevosErrores.fechaDesde = 'Debe seleccionar una fecha de comienzo de vigencia.'
    } else if (
      tienda.idTiendaOficialEstado === ESTADO_TEMPORAL &&
      !validarRangoFechas(new Date(), tienda.fechaDesde, 'day')
    ) {
      nuevosErrores.fechaDesde = 'La fecha de inicio debe ser igual o posterior a la fecha actual.'
    }

    if (!tienda.fechaHasta) {
      nuevosErrores.fechaHasta = 'La fecha de fin de vigencia es obligatoria.'
    } else if (!validarRangoFechas(new Date(), tienda.fechaHasta, 'day')) {
      nuevosErrores.fechaHasta = 'La fecha de fin de vigencia no puede estar en el pasado.'
    } else if (!validarRangoFechas(tienda.fechaDesde, tienda.fechaHasta, 'day')) {
      nuevosErrores.fechaHasta = 'La fecha de fin debe ser igual o posterior a la de inicio.'
    }

    if (
      tienda.idTiendaOficialEstado === ESTADO_VENCIDA &&
      validarRangoFechas(new Date(), tienda.fechaHasta, 'day')
    ) {
      nuevosErrores.idTiendaOficialEstado =
        'No se puede cambiar el estado a vencida si la fecha de fin de vigencia es futura.'
    }

    return nuevosErrores
  }

  const guardarCambiosTienda = async () => {
    try {
      await tiendasOficialesService.actualizarTienda({
        ...tienda,
        idTiendaOficialEstado:
          tienda.idTiendaOficialEstado === ESTADO_TEMPORAL
            ? ESTADO_ACTIVA
            : tienda.idTiendaOficialEstado
      })
      await publicacionesService.actualizarExcluidosDeTiendaOficial(idUsuario, idsExcluidos)
      const uploads = Array.isArray(formData) ? formData : []
      await Promise.all(uploads.map((data) => tiendasOficialesService.subirArchivo(data)))
      navigate(`/Publicaciones/TiendasOficiales/${tienda.idUsuario}`, {
        state: { vieneDeAdministrarTienda: true, idVendedor: tienda.idUsuario }
      })
    } catch (error) {
      const mensaje = `Ocurrió un error al actualizar tienda.`
      toastErrorLog(error, mensaje, 'tiendaOficial')
    }
  }

  const guardarCambioEstado = async () => {
    try {
      await tiendasOficialesService.actualizarTienda({
        ...tiendaOficialInicial.current,
        idTiendaOficialEstado: tienda.idTiendaOficialEstado,
        fechaHasta: formatearFecha(new Date(), 'ISO')
      })
      navigate(`/Publicaciones`)
    } catch (error) {
      const mensaje = `Ocurrió un error al actualizar tienda.`
      toastErrorLog(error, mensaje, 'tiendaOficial')
    }
  }

  const confirmarModal = async () => {
    try {
      if (tienda.idTiendaOficialEstado === ESTADO_VENCIDA) {
        await guardarCambioEstado()
      } else {
        await guardarCambiosTienda()
      }

      dispatch(setModal({ show: false }))
    } catch (error) {
      const mensaje = `Ocurrió un error al actualizar tienda.`
      toastErrorLog(error, mensaje, 'tiendaOficial')
    }
  }

  const abrirModalConfirmacion = (title, body, modalId) => {
    dispatch(
      setModal({
        size: 'sm',
        title,
        type: 'botones',
        body,
        show: true,
        modalId
      })
    )
  }

  const cancelarModal = () => {
    dispatch(setModal({ show: false }))
    if (tienda.idTiendaOficialEstado === ESTADO_VENCIDA) {
      setTienda((prev) => ({
        ...prev,
        idTiendaOficialEstado: tiendaOficialInicial.current.idTiendaOficialEstado
      }))
    }
  }

  const confirmarTienda = async () => {
    const nuevosErrores = validarForm()
    setErrores(nuevosErrores)
    if (Object.keys(nuevosErrores).length > 0) {
      return
    }
    abrirModalConfirmacion(
      'Confirmar Tienda',
      '¿Está seguro que desea guardar los cambios realizados en la tienda?',
      'confirmar-tienda'
    )
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setTienda((prev) => ({
      ...prev,
      [name]: value
    }))

    if (name === 'idTiendaOficialEstado' && Number(value) === ESTADO_VENCIDA) {
      const title = 'Cambio de Estado'
      const body =
        '¿Esta seguro que desea cambiar el estado de su tienda a VENCIDO? La tienda oficial cambiara la "Fecha de Vigencia hasta" al dia actual'
      const modalId = 'cambio-estado-tienda-oficial'
      abrirModalConfirmacion(title, body, modalId)
    }

    if (errores[name]) {
      setErrores((prev) => {
        const nuevosErrores = { ...prev }
        delete nuevosErrores[name]
        return nuevosErrores
      })
    }
  }

  const toggleExcluida = (idPublicacion) => {
    setIdsExcluidos((prev) =>
      prev.includes(idPublicacion)
        ? prev.filter((id) => id !== idPublicacion)
        : [...prev, idPublicacion]
    )
  }

  const selectDeshabilitado = !validarRangoFechas(new Date(), tienda?.fechaHasta, 'day')

  return (
    <div className={styles.pageWrapper} ref={topRef}>
      <div className={styles.pageHeader}>
        <h2 className={styles.pageTitle}>Administración de Tienda Oficial</h2>
        {tienda?.idTiendaOficialEstado === ESTADO_ACTIVA && (
          <LinkButton
            className={styles.btnIrATienda}
            to={{
              pathname: `/TiendasOficiales`,
              state: {
                vieneDeAdministrarTienda: true,
                idVendedor: tienda?.idUsuario,
                tienda: tienda
              }
            }}
          >
            <i className="bi bi-shop"></i>
            Ir a mi tienda
          </LinkButton>
        )}
      </div>
      <form onSubmit={(e) => e.preventDefault()}>
        <div className={styles.cardsRow}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Nombre de Fantasía</h3>
            </div>
            <div className={styles.cardBody}>
              <Input
                type="text"
                name="nombreTienda"
                placeholder="Ingresa el nombre de la Tienda"
                onChange={handleChange}
                value={tienda?.nombreTienda || ''}
                className={`${styles.input} input-text-bordered${errores.nombreTienda ? ` ${styles.inputError}` : ''}`}
              />
              {errores.nombreTienda && (
                <span className={styles.errorMsg}>{errores.nombreTienda}</span>
              )}
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Empresa</h3>
            </div>
            <div className={`${styles.cardBody} ${styles.cardBodyRow}`}>
              <div className={styles.fieldGroup}>
                <span className={styles.fieldLabel}>Razón Social</span>
                <span className={styles.fieldValue}>{tienda?.razonSocial || '—'}</span>
              </div>
              <div className={styles.fieldGroup}>
                <span className={styles.fieldLabel}>CUIT / CUIL</span>
                <span className={styles.fieldValue}>{tienda?.cuit || '—'}</span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.cardsRow}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Descripción de tu tienda</h3>
            </div>
            <div className={styles.cardBody}>
              <span className={styles.textareaLabel}>
                Máximo {MAX_CARACTERES_DESCRIPCION} caracteres
              </span>
              <textarea
                name="descripcionTienda"
                value={tienda?.descripcionTienda || ''}
                spellCheck="true"
                rows="5"
                className={`${styles.textarea} input-text-bordered${errores.descripcionTienda ? ` ${styles.inputError}` : ''}`}
                placeholder="Contá a tus clientes de qué trata la tienda..."
                onChange={handleChange}
              />
              {errores.descripcionTienda && (
                <span className={styles.errorMsg}>{errores.descripcionTienda}</span>
              )}
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Vigencia</h3>
            </div>
            <div className={styles.cardBody}>
              <FechaSelect
                label="Vigencia Desde"
                claveFecha="fechaDesde"
                fecha={tienda?.fechaDesde.slice(0, FECHA) || ''}
                guardarFecha={(e) => handleChange({ target: { name: 'fechaDesde', value: e } })}
                errorFecha={!!errores.fechaDesde}
                errorMensaje={errores.fechaDesde}
                isSelected={!!tienda?.fechaDesde}
                muestraEliminar={false}
              />
              <FechaSelect
                label="Vigencia Hasta"
                claveFecha="fechaHasta"
                fecha={tienda?.fechaHasta.slice(0, FECHA) || ''}
                guardarFecha={(e) => handleChange({ target: { name: 'fechaHasta', value: e } })}
                errorFecha={!!errores.fechaHasta}
                errorMensaje={errores.fechaHasta}
                isSelected={!!tienda?.fechaHasta}
                muestraEliminar={false}
              />
            </div>
          </div>
          {tienda?.idTiendaOficialEstado !== ESTADO_TEMPORAL && (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <h3>Estado</h3>
              </div>
              <div className={styles.cardBody}>
                <GenericSelect
                  name="idTiendaOficialEstado"
                  onChange={(value) =>
                    handleChange({
                      target: { name: 'idTiendaOficialEstado', value: Number(value) }
                    })
                  }
                  value={tienda?.idTiendaOficialEstado}
                  className={styles.select}
                  options={ESTADOS.map((e) => ({
                    value: e.idTiendaOficialEstado,
                    label: e.nombre,
                    key: e.idTiendaOficialEstado
                  }))}
                  errores={errores}
                  placeholder="Seleccionar estado"
                  disabled={selectDeshabilitado}
                />
                {selectDeshabilitado && (
                  <small className="warningMessage">
                    Para modificar el estado de la tienda, es necesario actualizar la fecha de
                    vigencia a un día posterior al actual.
                  </small>
                )}
              </div>
            </div>
          )}
        </div>
        <div className={styles.cardsRow}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Logo</h3>
            </div>
            <div className={styles.cardBody}>
              {tienda?.idTiendaOficial && (
                <Upload
                  idTiendaOficial={tienda?.idTiendaOficial}
                  imagenPath={tienda?.logoPath || ''}
                  idAdjunto={tienda?.idAdjuntoLogo}
                  esBanner={false}
                  setFormData={setFormData}
                />
              )}
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Banner</h3>
            </div>
            <div className={styles.cardBody}>
              {tienda?.idTiendaOficial && (
                <Upload
                  idTiendaOficial={tienda?.idTiendaOficial}
                  imagenPath={tienda?.bannerPath || ''}
                  idAdjunto={tienda?.idAdjuntoBanner}
                  esBanner={true}
                  setFormData={setFormData}
                />
              )}
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>Excluir publicaciones</h3>
            </div>
            <div className={styles.cardBody}>
              <span className={styles.fieldLabel}>
                Las publicaciones seleccionadas no serán incluidas en la tienda oficial
              </span>
              <div className={styles.listaExcluir}>
                {publicaciones.map((p) => {
                  const excluida = idsExcluidos.includes(p.idPublicacion)
                  return (
                    <label
                      key={p.idPublicacion}
                      className={`${styles.itemExcluir} ${excluida ? styles.itemExcluirSeleccionado : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={excluida}
                        onChange={() => toggleExcluida(p.idPublicacion)}
                      />
                      <span>
                        <span className={styles.itemExcluirId}>#{p.idPublicacion}</span>
                        {p.titulo.length > MAX_CARACTERES_TITULO_PUB
                          ? `${p.titulo.slice(0, MAX_CARACTERES_TITULO_PUB)}…`
                          : p.titulo}
                      </span>
                    </label>
                  )
                })}
              </div>
              {idsExcluidos.length > 0 ? (
                <>
                  {' '}
                  <div className={` ${styles.rowExcluirPublicaciones}`}>
                    <span className={styles.contadorExcluidas}>
                      {idsExcluidos.length}
                      {idsExcluidos.length > 1
                        ? ' publicaciones excluidas'
                        : ' publicación excluida'}
                    </span>
                    <button className={styles.btnDestildar} onClick={() => setIdsExcluidos([])}>
                      Destildar todas
                    </button>
                  </div>
                </>
              ) : (
                <span className={styles.contadorExcluidas}>No hay publicaciones excluidas</span>
              )}
            </div>
          </div>
        </div>
        <GenericModal
          modalId={
            tienda?.idTiendaOficialEstado === ESTADO_VENCIDA
              ? 'cambio-estado-tienda-oficial'
              : 'confirmar-tienda'
          }
          onConfirm={confirmarModal}
          onClose={cancelarModal}
        />
        <div className={styles.footer}>
          <LinkButton className={styles.btnConfirmar} onClick={confirmarTienda} disabled={!tienda}>
            Confirmar
          </LinkButton>
        </div>
      </form>
    </div>
  )
}

export default ConvertirTiendaOficial
